import { addSheetBackLink, PARENT_SHEETS } from "../sheetNavigation";
import {
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_I,
  COL_J,
  COL_K,
  COL_L,
  COL_M,
  COL_N,
  HEADER_FILL,
  TABLE_HEADER_FILL,
  formatDischarge,
  formatNumber,
  getFyLabels,
  mergeRow,
  readFY,
  safeText,
  setCell,
  sumDischargeSources,
  sumFYCells,
} from "./principle6SheetHelpers";

const TITLE_ROW = 3;
const HEADER_ROW = 4;

export const SHEET_NAME = "Plant in area of water stress";

const SHEET_TITLE =
  "Water withdrawal, consumption and discharge in areas of water stress (in kilolitres)";

const WITHDRAWAL_ROWS = [
  ["(i) Surface water", "surfaceWater"],
  ["(ii) Groundwater", "groundWater"],
  ["(iii) Third party water", "thirdPartyWater"],
  ["(iv) Seawater / desalinated water", "seaWater"],
  ["(v) Others", "other"],
];

const DISCHARGE_SOURCES = [
  ["To surface water", "toSurfaceWater"],
  ["To Groundwater", "toGroundwater"],
  ["To sea water", "toProducedWater"],
  ["Sent to third parties", "toThirdParties"],
  ["Others", "others"],
];

function addWithdrawalTable(ws, startRow, facility, fyCurrent, fyPrevious) {
  let row = startRow;
  mergeRow(ws, row, COL_D, COL_L, "Water withdrawal by source (in kiloliters)", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "left",
  });
  row += 1;

  mergeRow(ws, row, COL_D, COL_G, "Parameter", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_H, COL_I, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_J, COL_L, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const w = facility?.waterWithdrawal || {};
  const sumKeys = WITHDRAWAL_ROWS.map(([, key]) => key);

  WITHDRAWAL_ROWS.forEach(([label, key]) => {
    mergeRow(ws, row, COL_D, COL_G, label, { align: "left" });
    mergeRow(ws, row, COL_H, COL_I, formatNumber(readFY(w[key], "current")), { align: "center" });
    mergeRow(ws, row, COL_J, COL_L, formatNumber(readFY(w[key], "previous")), { align: "center" });
    row += 1;
  });

  mergeRow(ws, row, COL_D, COL_G, "Total volume of water withdrawal", { align: "left", bold: true });
  mergeRow(ws, row, COL_H, COL_I, formatNumber(sumFYCells(w, sumKeys, "current")), { align: "center" });
  mergeRow(ws, row, COL_J, COL_L, formatNumber(sumFYCells(w, sumKeys, "previous")), { align: "center" });
  row += 1;

  const tailRows = [
    ["Total volume of water consumption (in kiloliters)", "totalWaterConsumption"],
    ["Water intensity per rupee of turnover (Water consumed / turnover)", "waterIntensityPerRupee"],
    [
      "Water intensity (optional) - the relevant metric may be selected by the entity",
      "waterIntensity",
    ],
  ];
  tailRows.forEach(([label, key]) => {
    mergeRow(ws, row, COL_D, COL_G, label, { align: "left" });
    mergeRow(ws, row, COL_H, COL_I, formatNumber(readFY(w[key], "current")), { align: "center" });
    mergeRow(ws, row, COL_J, COL_L, formatNumber(readFY(w[key], "previous")), { align: "center" });
    row += 1;
  });

  return row;
}

function addDischargeTable(ws, startRow, facility, fyCurrent, fyPrevious) {
  ["G", "H", "I", "J"].forEach((col) => {
    ws.getColumn(col).width = 14;
  });

  let row = startRow;
  mergeRow(ws, row, COL_D, COL_N, "Water discharge by destination and level of treatment (in kilolitres)", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "left",
  });
  row += 1;

  mergeRow(ws, row, COL_D, COL_E, "Parameter", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_G, COL_H, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_I, COL_J, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  mergeRow(ws, row, COL_D, COL_E, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_F, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_G, "Water Discharge (in KL)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "Specify level of treatment", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "Water Discharged (in KL)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "Specify level of treatment", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  DISCHARGE_SOURCES.forEach(([label, key]) => {
    const c = facility?.waterDischargeCurrent?.[key] || {};
    const p = facility?.waterDischargePrevious?.[key] || {};

    mergeRow(ws, row, COL_D, COL_E, label, { align: "left" });
    setCell(ws, row, COL_F, "Not treated", { align: "left" });
    setCell(ws, row, COL_G, formatDischarge(c.dischargeWithoutTreatment), { align: "center" });
    setCell(ws, row, COL_H, "-", { align: "center" });
    setCell(ws, row, COL_I, formatDischarge(p.dischargeWithoutTreatment), { align: "center" });
    setCell(ws, row, COL_J, "-", { align: "center" });
    row += 1;

    mergeRow(ws, row, COL_D, COL_E, "-", { align: "left" });
    setCell(ws, row, COL_F, "With treatment", { align: "left" });
    setCell(ws, row, COL_G, formatDischarge(c.dischargeAfterTreatment), { align: "center" });
    setCell(ws, row, COL_H, safeText(c.treatmentLevel ?? c.treatMentLeval, ""), { align: "center" });
    setCell(ws, row, COL_I, formatDischarge(p.dischargeAfterTreatment), { align: "center" });
    setCell(ws, row, COL_J, safeText(p.treatmentLevel ?? p.treatMentLeval, ""), { align: "center" });
    row += 1;
  });

  const sumKeys = DISCHARGE_SOURCES.map(([, key]) => key);
  mergeRow(ws, row, COL_D, COL_F, "Total water discharged", { align: "left", bold: true });
  mergeRow(
    ws,
    row,
    COL_G,
    COL_H,
    formatDischarge(sumDischargeSources(facility?.waterDischargeCurrent, sumKeys)),
    { align: "center" }
  );
  mergeRow(
    ws,
    row,
    COL_I,
    COL_J,
    formatDischarge(sumDischargeSources(facility?.waterDischargePrevious, sumKeys)),
    { align: "center" }
  );
  row += 1;

  return row;
}

export function buildPlantInAreaOfWaterStressSheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet(SHEET_NAME, { views: [{ showGridLines: true }] });
  const { current: fyCurrent, previous: fyPrevious } = getFyLabels(reportingYear);
  const facilities = sectionC?.principle6?.leadershipIndicator?.waterWithDrawalConsumptionAndDischarg || [];
  const dataRows = Array.isArray(facilities) && facilities.length ? facilities : [{}];

  ws.getColumn("A").width = 2;
  ws.getColumn("B").width = 2;
  ws.getColumn("C").width = 2;
  ws.getColumn("D").width = 10;
  ws.getColumn("E").width = 24;
  ws.getColumn("F").width = 18;
  ws.getColumn("G").width = 14;
  ws.getColumn("H").width = 14;
  ws.getColumn("I").width = 14;
  ws.getColumn("J").width = 14;
  ws.getColumn("K").width = 14;
  ws.getColumn("L").width = 14;
  ws.getColumn("M").width = 14;
  ws.getColumn("N").width = 14;

  addSheetBackLink(ws, PARENT_SHEETS.PRINCIPLE_6, { mergeEnd: COL_L });

  mergeRow(ws, TITLE_ROW, COL_D, COL_L, SHEET_TITLE, {
    bold: true,
    fill: HEADER_FILL,
    align: "center",
  });

  let row = HEADER_ROW;
  dataRows.forEach((facility, idx) => {
    mergeRow(ws, row, COL_D, COL_L, `Facility ${idx + 1}: ${safeText(facility?.facilityName)}`, {
      bold: true,
      fill: TABLE_HEADER_FILL,
      align: "left",
    });
    row += 1;
    mergeRow(ws, row, COL_D, COL_L, `(i) Name of area: ${safeText(facility?.nameOfArea)}`, { align: "left" });
    row += 1;
    mergeRow(ws, row, COL_D, COL_L, `(ii) Nature of Operation: ${safeText(facility?.operation)}`, { align: "left" });
    row += 1;
    row = addWithdrawalTable(ws, row, facility, fyCurrent, fyPrevious);
    row = addDischargeTable(ws, row, facility, fyCurrent, fyPrevious);
    row += 1;
  });

  return ws;
}
